import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, Clock, XCircle, CheckCircle2, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { API } from '../App';

const istTodayISO = () => {
  const d = new Date();
  const istOffsetMin = 5 * 60 + 30;
  const localOffsetMin = d.getTimezoneOffset();
  const ist = new Date(d.getTime() + (istOffsetMin + localOffsetMin) * 60000);
  return `${ist.getFullYear()}-${String(ist.getMonth() + 1).padStart(2, '0')}-${String(ist.getDate()).padStart(2, '0')}`;
};

const istNowHour = () => {
  const d = new Date();
  const istOffsetMin = 5 * 60 + 30;
  const localOffsetMin = d.getTimezoneOffset();
  const ist = new Date(d.getTime() + (istOffsetMin + localOffsetMin) * 60000);
  return ist.getHours();
};

const ManageAppointmentPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [appt, setAppt] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [rescheduling, setRescheduling] = useState(false);
  const [mode, setMode] = useState('view'); // 'view' | 'reschedule'
  const [monthOffset, setMonthOffset] = useState(0);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSlot, setSelectedSlot] = useState('');
  const [availability, setAvailability] = useState(null);
  const [availLoading, setAvailLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [ap, cf] = await Promise.all([
          axios.get(`${API}/appointments/cancel/${token}`),
          axios.get(`${API}/appointments/config`),
        ]);
        setAppt(ap.data);
        setConfig(cf.data);
      } catch (e) {
        toast.error('Invalid or expired link');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  useEffect(() => {
    if (!selectedDate) return;
    setAvailLoading(true);
    axios.get(`${API}/appointments/availability?date=${selectedDate}`)
      .then(res => setAvailability(res.data))
      .catch(() => toast.error('Could not load slot availability'))
      .finally(() => setAvailLoading(false));
  }, [selectedDate]);

  const handleCancel = async () => {
    if (!window.confirm('Cancel this appointment?')) return;
    setCancelling(true);
    try {
      await axios.post(`${API}/appointments/cancel/${token}`);
      setCancelled(true);
      toast.success('Appointment cancelled');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not cancel');
    } finally {
      setCancelling(false);
    }
  };

  const calendarGrid = useMemo(() => {
    if (!config) return null;
    const todayIso = istTodayISO();
    const today = new Date(todayIso + 'T00:00:00');
    const first = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
    const label = first.toLocaleString('en-IN', { month: 'long', year: 'numeric' });
    const daysInMonth = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
    const startWeekday = first.getDay();
    const cells = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    const maxDate = new Date(today);
    maxDate.setDate(today.getDate() + (config.booking_window_days || 60));
    const openDays = new Set(config.open_days || [0, 1, 2, 3, 4, 5]);
    const blocked = new Set(config.blocked_dates || []);
    for (let d = 1; d <= daysInMonth; d++) {
      const dt = new Date(first.getFullYear(), first.getMonth(), d);
      const iso = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
      const backendDow = (dt.getDay() + 6) % 7;
      const isPast = iso < todayIso;
      const inWindow = dt <= maxDate;
      const enabled = !isPast && inWindow && openDays.has(backendDow) && !blocked.has(iso);
      cells.push({ day: d, iso, enabled, isPast });
    }
    return { label, cells };
  }, [config, monthOffset]);

  const submitReschedule = async () => {
    if (!selectedDate || !selectedSlot) { toast.error('Please pick a new date & time'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/appointments/reschedule/${token}`, { date: selectedDate, time_slot: selectedSlot });
      toast.success('Appointment rescheduled');
      setAppt({ ...appt, date: res.data.date, time_slot: res.data.time_slot });
      setRescheduling(true);
      setMode('view');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not reschedule');
    } finally {
      setSubmitting(false);
    }
  };

  const todayIso = istTodayISO();
  const currentHour = istNowHour();

  return (
    <div className="min-h-screen bg-accent flex items-center justify-center py-16 px-4" data-testid="manage-appointment-page">
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="bg-white border border-border max-w-2xl w-full p-8 sm:p-10">
        {loading ? (
          <p className="text-muted-foreground text-center">Loading…</p>
        ) : !appt ? (
          <div className="text-center">
            <XCircle size={60} className="text-red-500 mx-auto mb-4" />
            <h1 className="font-heading text-2xl mb-3">Link not valid</h1>
            <p className="text-sm text-muted-foreground mb-6">This appointment link may have expired or been removed.</p>
            <button onClick={() => navigate('/')} className="px-6 py-3 bg-primary text-primary-foreground">Return Home</button>
          </div>
        ) : cancelled || appt.status === 'cancelled' ? (
          <div className="text-center">
            <CheckCircle2 size={60} className="text-primary mx-auto mb-4" />
            <h1 className="font-heading text-2xl mb-3">Appointment cancelled</h1>
            <p className="text-sm text-muted-foreground mb-6">The slot has been freed. We hope to welcome you another time.</p>
            <button onClick={() => navigate('/book')} className="px-6 py-3 bg-primary text-primary-foreground">Book Another</button>
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              <p className="text-xs uppercase tracking-[0.3em] text-primary mb-3">— Manage Appointment —</p>
              <h1 className="font-heading text-2xl">Your Booking</h1>
              {rescheduling && <p className="text-xs text-primary mt-2">Rescheduled successfully — a new confirmation email is on its way.</p>}
            </div>
            <div className="border border-border p-5 mb-6 text-sm space-y-2">
              <p><strong>Name:</strong> {appt.full_name}</p>
              <p className="flex items-center gap-2"><Calendar size={14} /> {appt.date}</p>
              <p className="flex items-center gap-2"><Clock size={14} /> {appt.time_slot} IST</p>
            </div>

            {mode === 'view' ? (
              <div className="flex flex-col sm:flex-row gap-3">
                <button onClick={() => setMode('reschedule')} className="flex-1 py-3 bg-primary text-primary-foreground hover:bg-primary/90 flex items-center justify-center gap-2" data-testid="reschedule-btn">
                  <RefreshCw size={16} /> Reschedule
                </button>
                <button onClick={handleCancel} disabled={cancelling} className="flex-1 py-3 border border-red-500 text-red-600 hover:bg-red-50 disabled:opacity-50" data-testid="cancel-appointment-btn">
                  {cancelling ? 'Cancelling…' : 'Cancel Appointment'}
                </button>
              </div>
            ) : (
              <div data-testid="reschedule-section">
                <p className="text-sm text-muted-foreground mb-4">Choose a new date &amp; time. Your details will stay the same.</p>
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <button type="button" onClick={() => setMonthOffset(monthOffset - 1)} disabled={monthOffset === 0}
                      className="p-2 border border-border hover:border-primary disabled:opacity-30"><ChevronLeft size={16} /></button>
                    <p className="font-heading text-lg">{calendarGrid?.label}</p>
                    <button type="button" onClick={() => setMonthOffset(monthOffset + 1)} className="p-2 border border-border hover:border-primary"><ChevronRight size={16} /></button>
                  </div>
                  <div className="grid grid-cols-7 gap-1 text-center text-xs text-muted-foreground mb-2">
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => <div key={d} className="py-2">{d}</div>)}
                  </div>
                  <div className="grid grid-cols-7 gap-1">
                    {(calendarGrid?.cells || []).map((c, i) => c ? (
                      <button
                        key={i}
                        type="button"
                        disabled={!c.enabled}
                        onClick={() => { setSelectedDate(c.iso); setSelectedSlot(''); }}
                        className={`aspect-square text-sm border transition-all ${
                          selectedDate === c.iso ? 'bg-primary text-primary-foreground border-primary'
                            : c.enabled ? 'border-border hover:border-primary'
                            : c.isPast ? 'border-transparent text-muted-foreground/30 bg-muted/40 cursor-not-allowed line-through'
                            : 'border-transparent text-muted-foreground/40 cursor-not-allowed'
                        }`}
                        data-testid={c.enabled ? `resch-date-${c.iso}` : undefined}
                      >{c.day}</button>
                    ) : <div key={i} />)}
                  </div>
                </div>

                {selectedDate && (
                  <div className="border-t border-border pt-5 mb-6">
                    <p className="text-sm font-medium mb-3 flex items-center gap-2"><Clock size={14} /> Time slot ({selectedDate}{selectedDate === todayIso ? ' — Today' : ''})</p>
                    {availLoading ? (
                      <p className="text-sm text-muted-foreground">Checking availability…</p>
                    ) : (
                      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                        {(availability?.slots || []).map(s => {
                          const isPastToday = selectedDate === todayIso && parseInt(s.slot.split(':')[0], 10) <= currentHour;
                          const disabled = !s.available || isPastToday;
                          const label = !s.available && !isPastToday ? 'Booked' : isPastToday ? 'Passed' : null;
                          return (
                            <button
                              key={s.slot}
                              type="button"
                              disabled={disabled}
                              onClick={() => setSelectedSlot(s.slot)}
                              className={`px-3 py-3 border text-sm transition-colors ${
                                selectedSlot === s.slot ? 'bg-primary text-primary-foreground border-primary'
                                  : disabled ? 'border-border bg-muted/50 text-muted-foreground/60 cursor-not-allowed'
                                  : 'border-border hover:border-primary'
                              }`}
                              data-testid={`resch-slot-${s.slot}`}
                            >
                              {s.slot}
                              {label && <span className="block text-[10px] mt-0.5">{label}</span>}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-3">
                  <button onClick={() => setMode('view')} className="flex-1 py-3 border border-border hover:border-primary">Back</button>
                  <button onClick={submitReschedule} disabled={submitting || !selectedDate || !selectedSlot} className="flex-1 py-3 bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50" data-testid="confirm-reschedule-btn">
                    {submitting ? 'Rescheduling…' : 'Confirm New Time'}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </motion.div>
    </div>
  );
};

export default ManageAppointmentPage;
